import { Basket, BasketResponse } from '@model/iceberg';
import { AsyncData } from '@model/common';
import { Extras } from '@model/iceberg/booking/extras';
import { BookingApi } from '@model/iceberg/service/booking';
import { BasketActions, getBasket, receiveBasketSuccess } from '@state/basket';
import { FlightApi } from '@model/iceberg/service/booking/flight-api';
import { fetchFromTourReference, TourReferencesActions } from '@state/slices/tours/tour-references';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '@state/store';
import { DealFinderResultsActions } from '@state/slices/deal-finder-results/dealFinderResultsSlice';

export enum ExtrasActions {
  NAME = '@EXTRAS',
  FETCH_EXTRAS = '@EXTRAS/FETCH_EXTRAS',
  CLEAR_EXTRAS = '@EXTRAS/clearExtras',
  RECEIVE_EXTRAS_SUCCESS = '@EXTRAS/RECEIVE_EXTRAS_SUCCESS',
  RECEIVE_EXTRAS_FAILURE = '@EXTRAS/RECEIVE_EXTRAS_FAILURE'
}

export const INITIAL_EXTRAS_STATE: AsyncData<Extras> = {
  data: {
    transfers: [],
    luggageExtras: [],
    optionalSupplements: []
  },
  loading: false,
  error: null
};

export const fetchExtras = createAsyncThunk(
  ExtrasActions.FETCH_EXTRAS,
  async (payload: string, { dispatch, getState, rejectWithValue }) => {
    const bookingApi = new BookingApi();
    const flightApi = new FlightApi();

    try {
      const response: Extras = await bookingApi.getExtras(payload);
      return response;
    } catch (error: any) {
      if (error.errorCode === 400) {
        try {
          const state = getState() as RootState;
          const basket: Basket = getBasket(state);
          const flightToken = basket?.flight?.token;

          if (flightToken) {
            const basketResponse: BasketResponse = await flightApi.put(payload, flightToken, true);
            dispatch(receiveBasketSuccess(basketResponse));

            const retryResponse: Extras = await bookingApi.getExtras(payload);
            return retryResponse;
          }
        } catch (retryError: any) {
          return rejectWithValue(retryError.message);
        }
      }

      return rejectWithValue(error.message);
    }
  }
);

const extrasSlice = createSlice({
  name: ExtrasActions.NAME,
  initialState: INITIAL_EXTRAS_STATE,
  reducers: {
    clearExtras: () => INITIAL_EXTRAS_STATE
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExtras.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExtras.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchExtras.rejected, (state, action) => {
        state.data = INITIAL_EXTRAS_STATE.data;
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(BasketActions.ADD_FLIGHT_TO_BASKET, () => INITIAL_EXTRAS_STATE)
      .addCase(DealFinderResultsActions.CLEAR_RESULTS, () => INITIAL_EXTRAS_STATE)
      .addCase(TourReferencesActions.SET_TOUR_REFERENCES, () => INITIAL_EXTRAS_STATE)
      .addCase(fetchFromTourReference.fulfilled, () => INITIAL_EXTRAS_STATE);
  }
});

export const { clearExtras } = extrasSlice.actions;
export const extrasReducer = extrasSlice.reducer;
